import React, { useState, useEffect, useCallback, FC } from 'react';
import styled from '@emotion/styled';
import { withRouter } from 'react-router';
import _ from 'lodash';

import * as actions from '~actions';
import Modules from '~Components/Modules';
import LoginModal from '../Tables/Components/LoginModal';
import BongMaster from '../Tables/Components/BongMaster';

import Onboarding from '~Scenes/Onboarding';
import { useSelector, useDispatch, useI18n } from '~/Hooks';

import Instance from './Components/Instance';
import TabBar from './Components/TabBar';
import CartProvider from '~Components/CartProvider';
import QuestionModal from '~ComponentsImproved/QuestionModal';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--colorBlueGray1100);
  z-index: 1;
`;

export interface InstanceState {
  id: number;
  orderId: string | null;
  easyId: string | null;
}

let instanceCounter = 1;

const MultiQSR: FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const { i18n } = useI18n();
  const staffId = useSelector((state) => state.staff?.id);
  const dispatch = useDispatch();
  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const [collectQuestionModal, setCollectQuestionModal] = useState<boolean>(false);
  // Store easyId (order number) across instance replacements when collecting
  const [collectEasyId, setCollectEasyId] = useState<string | null>(null);

  // Set last used QSR mode (multi vs single)
  useEffect(() => {
    dispatch(actions.setLastUsedPosMode('MULTI'));
  }, []);

  /**
   * An instance is one mounted "QSR" / "Bong" / "Tab".
   * For example if the staff is taking orders from two customers at
   * once, we would have two instances
   */
  function createInstance(noIncrement = false) {
    return {
      id: noIncrement ? instanceCounter : ++instanceCounter,
      orderId: null,
      easyId: null,
    };
  }

  // Active index = which one are we looking at right now
  const [activeInstanceIndex, setActiveInstanceIndex] = useState(0);
  const [instances, setInstances] = useState<InstanceState[]>([createInstance(true)]);

  const removeInstance = (id: number) => {
    const index = instances.findIndex((i) => i.id === id);
    setInstances((inst) => inst.filter((i) => i.id !== id));

    if (index <= activeInstanceIndex) {
      setActiveInstanceIndex(Math.max(0, activeInstanceIndex - 1));
    }
  };

  /**
   * Update the Order ID of a specific order / instance
   */
  const updateOrderId = useCallback(
    (id: number, orderId: string | null) => {
      setInstances((i) =>
        i.map((instance) => {
          if (instance.id === id) {
            return {
              ...instance,
              orderId,
            };
          }

          return instance;
        }),
      );
    },
    [instances],
  );

  const onInstanceCompleted = useCallback(
    (id: number) => {
      // If we only have one instance, create a new one to replace it, otherwise just remove it
      if (instances.length === 1) {
        // If we are collecting, check if the user wants to keep collecting
        if (bongPrintingHalted) {
          setCollectQuestionModal(true);
        }

        setInstances((inst) => [...inst, createInstance()]);
        setActiveInstanceIndex(instances.length);

        setTimeout(() => {
          removeInstance(id);
        }, 750);
      } else {
        removeInstance(id);
      }
    },
    [instances, bongPrintingHalted],
  );

  const onPaymentInitiated = useCallback(
    (id: number) => {
      if (instances.length >= 5) {
        return;
      }

      setInstances([...instances, createInstance()]);

      // First add, then update index. This will make it so the new
      // instance plays a little entry animation
      setTimeout(() => {
        setActiveInstanceIndex(instances.length);
      }, 1);
    },
    [instances],
  );

  const keepCollectingBongs = () => {
    setCollectQuestionModal(false);
  };

  const resumeBongPrinting = () => {
    setCollectQuestionModal(false);
    setCollectEasyId(null);
    dispatch({ type: 'RESUME_BONG_PRINTING' });
  };

  return (
    <Wrapper>
      {/**
       * The tab bar shows each instance as a tab and allows switching between them
       */}
      <TabBar
        instances={instances}
        currentIndex={activeInstanceIndex}
        onTabClicked={(index) => setActiveInstanceIndex(index)}
        onNewTab={() => {
          setInstances([...instances, createInstance()]);

          // First add, then update index. This will make it so the new
          // instance plays a little entry animation
          setTimeout(() => {
            setActiveInstanceIndex(instances.length);
          }, 1);
        }}
        onCloseTab={(id) => {
          removeInstance(id);
        }}
      />

      {/**
       * This is the actual mounted instance of the bong component etc
       */}
      {instances.map((instance, index) => (
        <CartProvider key={instance.id} useSeparateTakeAwayMenu>
          <Instance
            id={instance.id}
            active={index === activeInstanceIndex}
            isFirst={index === 0}
            onCompleted={onInstanceCompleted}
            onPaymentInitiated={onPaymentInitiated}
            onOrderIdChanged={updateOrderId}
            initialEasyId={bongPrintingHalted ? collectEasyId : null}
            onEasyIdChanged={setCollectEasyId}
          />
        </CartProvider>
      ))}

      <Modules />
      <BongMaster />
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      <Onboarding />

      {!staffId && <LoginModal />}

      <QuestionModal
        question={i18n('POS.CollectQuestion')}
        isShowing={collectQuestionModal}
        onLeftClick={resumeBongPrinting}
        onRightClick={keepCollectingBongs}
      />
    </Wrapper>
  );
};

export default withRouter(MultiQSR);
